import type { VideoFramesProvider, VideoFramesProviderFactory } from '../video/VideoFramesProvider';
import { WebpResource } from './WebpResource';
import { VideoFramesProviderResource } from './VideoFramesProviderResource';
import { observable } from '../reactivity';

type UnderlyingResource = ImageResourceAdapter | WebpResource | VideoFramesProviderResource;

const SVG_DEFAULT_SIZE = { width: 300, height: 150 };

function parseSvgCanonicalSize(svgText: string): { width: number; height: number } {
    const parsed = new DOMParser().parseFromString(svgText, 'image/svg+xml');
    const svg = parsed.documentElement;
    const attrWidth = parseFloat(svg.getAttribute('width') ?? '');
    const attrHeight = parseFloat(svg.getAttribute('height') ?? '');
    if (attrWidth > 0 && attrHeight > 0) {
        return { width: attrWidth, height: attrHeight };
    }
    const viewBox = svg.getAttribute('viewBox');
    if (viewBox) {
        const parts = viewBox.split(/[\s,]+/).map(parseFloat);
        if (parts[2]! > 0 && parts[3]! > 0) {
            return { width: parts[2]!, height: parts[3]! };
        }
    }
    return { ...SVG_DEFAULT_SIZE };
}

class ImageResourceAdapter {
    #img: HTMLImageElement;
    #sourceUrl: string;
    #isSvg: boolean;
    #load: Promise<this> | null = null;
    @observable accessor #width = 0;
    @observable accessor #height = 0;
    @observable accessor #loaded = false;
    @observable accessor #destroyed = false;

    constructor(sourceUrl: string, isSvg: boolean) {
        this.#sourceUrl = sourceUrl;
        this.#isSvg = isSvg;
        this.#img = new Image();
        this.#img.crossOrigin = 'anonymous';
    }

    get source(): HTMLImageElement | null {
        return this.#loaded && !this.#destroyed ? this.#img : null;
    }

    get width(): number {
        return this.#width;
    }

    get height(): number {
        return this.#height;
    }

    get valid(): boolean {
        return !this.#destroyed && this.#loaded;
    }

    get destroyed(): boolean {
        return this.#destroyed;
    }

    // eslint-disable-next-line @typescript-eslint/promise-function-async
    load(): Promise<this> {
        if (this.#load) {
            return this.#load;
        }
        this.#load = this.#loadImage();
        return this.#load;
    }

    async #loadImage(): Promise<this> {
        let canonicalSize: { width: number; height: number } | null = null;
        let src = this.#sourceUrl;
        if (this.#isSvg) {
            const svgText = await (await fetch(this.#sourceUrl)).text();
            canonicalSize = parseSvgCanonicalSize(svgText);
            const parsed = new DOMParser().parseFromString(
                svgText,
                'image/svg+xml',
            ).documentElement;
            parsed.setAttribute('width', String(canonicalSize.width));
            parsed.setAttribute('height', String(canonicalSize.height));
            src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(new XMLSerializer().serializeToString(parsed))}`;
        }
        await new Promise<void>((resolve, reject) => {
            this.#img.onload = () => resolve();
            this.#img.onerror = () => reject(new Error(`Failed to load image ${this.#sourceUrl}`));
            this.#img.src = src;
        });
        const size = canonicalSize ?? {
            width: this.#img.naturalWidth || this.#img.width,
            height: this.#img.naturalHeight || this.#img.height,
        };
        this.#width = size.width;
        this.#height = size.height;
        this.#loaded = true;
        return this;
    }

    destroy(): void {
        if (this.#destroyed) {
            return;
        }
        this.#destroyed = true;
        this.#img.removeAttribute('src');
    }
}

export type MediaResourceKind = 'video' | 'image' | 'svg' | 'webp';

/**
 * The pixels a media resource currently exposes. Anything here can be passed to
 * `drawImage`/`texImage2D`.
 */
export type MediaFrameSource = CanvasImageSource;

export type MediaResourceCreateParams = {
    sourceUrl: string | null;
    sourceMimeType: 'video/mp4' | null;
    isVideo: boolean;
    isSvg: boolean;
    sourceDuration: number | null;
    createVideoFramesProvider: VideoFramesProviderFactory;
};

/**
 * Encapsulates a single media asset resource behind one interface, hiding which concrete
 * PIXI resource (or {@link WebpResource}) backs it. This lets MediaAsset operate on media
 * without branching on the resource/asset type in every method.
 */
export class MediaResource {
    #resource: UnderlyingResource;
    #kind: MediaResourceKind;

    constructor(resource: UnderlyingResource, kind: MediaResourceKind) {
        this.#resource = resource;
        this.#kind = kind;
    }

    /**
     * Create the appropriate resource for the given source. The selection order matches the
     * legacy logic: animated WebP is detected first (even for video source data), then video,
     * then SVG, then a plain image.
     */
    static create({
        sourceUrl,
        sourceMimeType,
        isVideo,
        isSvg,
        sourceDuration,
        createVideoFramesProvider,
    }: MediaResourceCreateParams): MediaResource {
        if (WebpResource.isWebp(sourceUrl)) {
            if (!sourceUrl) {
                throw new Error('Cannot create webp resource without a source URL.');
            }
            return new MediaResource(new WebpResource(sourceUrl, sourceDuration), 'webp');
        }
        if (isVideo) {
            if (!sourceUrl) {
                throw new Error('Cannot create video resource without a source URL.');
            }
            if (!sourceMimeType) {
                throw new Error('Cannot create video resource without a source MIME type.');
            }
            const videoFramesProvider = createVideoFramesProvider({
                sourceUrl,
                mimeType: sourceMimeType,
            });
            videoFramesProvider.debugName = 'original';
            return new MediaResource(
                new VideoFramesProviderResource(videoFramesProvider, {
                    autoLoad: false,
                    autoPlay: false,
                }),
                'video',
            );
        }
        if (!sourceUrl) {
            throw new Error('Cannot create image resource without a source URL.');
        }
        if (isSvg) {
            return new MediaResource(new ImageResourceAdapter(sourceUrl, true), 'svg');
        }
        return new MediaResource(new ImageResourceAdapter(sourceUrl, false), 'image');
    }

    /**
     * The underlying resource. Prefer the wrapper's own methods; use this only where the
     * concrete resource object is genuinely required.
     */
    get raw(): UnderlyingResource {
        return this.#resource;
    }

    get kind(): MediaResourceKind {
        return this.#kind;
    }

    get isVideo(): boolean {
        return this.#kind === 'video';
    }

    get isWebp(): boolean {
        return this.#kind === 'webp';
    }

    get isImage(): boolean {
        return this.#kind === 'image';
    }

    get isSvg(): boolean {
        return this.#kind === 'svg';
    }

    get source(): MediaFrameSource | null {
        return (this.#resource.source ?? null) as MediaFrameSource | null;
    }

    get width(): number {
        return this.#resource.width;
    }

    get height(): number {
        return this.#resource.height;
    }

    get valid(): boolean {
        return this.#resource.valid;
    }

    get destroyed(): boolean {
        return this.#resource.destroyed;
    }

    /**
     * Return the underlying resource's load promise directly rather than wrapping it in an
     * `async` method. An `async` wrapper adds an extra microtask before the caller's `.then`
     * runs, which shifts the resulting redraw into a later build pass and reintroduces the
     * watermark create/destroy loop (see watermark_tests "does not create a loop..."). Keep this
     * non-async to preserve the original rebuild timing.
     */
    // eslint-disable-next-line @typescript-eslint/promise-function-async
    load(): Promise<unknown> {
        return this.#resource.load();
    }

    destroy(): void {
        this.#resource.destroy();
    }

    /**
     * The backing {@link VideoFramesProvider} when this resource is video-backed, otherwise null.
     * WebP resources are never considered video-backed even though they animate.
     */
    get videoFramesProvider(): VideoFramesProvider | null {
        if (!(this.#resource instanceof VideoFramesProviderResource)) {
            return null;
        }
        return this.#resource.provider;
    }

    /**
     * Swap the backing video frames provider. Used to switch the active playback region's video.
     */
    set videoFramesProvider(videoFramesProvider: VideoFramesProvider) {
        if (!(this.#resource instanceof VideoFramesProviderResource)) {
            throw new Error('Cannot set a video frames provider on a non-video resource.');
        }
        this.#resource.provider = videoFramesProvider;
    }

    /**
     * Move the current frame to the given time. Only meaningful for WebP; a no-op otherwise
     * because video/image resources are seeked through their DOM element.
     */
    seek(time: number): void {
        if (this.#resource instanceof WebpResource) {
            this.#resource.seek(time);
        }
    }

    /**
     * Buffer the frame(s) needed for the given time. Only meaningful for WebP; a no-op
     * otherwise because video buffering is driven through the DOM element.
     */
    async buffer(time: number): Promise<void> {
        if (this.#resource instanceof WebpResource) {
            await this.#resource.buffer(time);
        }
    }
}
